import {
  DataGrid,
  type GridColDef,
  type GridRowModel,
  type GridRowsProp,
  type GridSlotsComponent,
  type GridValidRowModel,
  type GridRowParams,
  type GridLocaleText,
} from "@mui/x-data-grid";
import { esES } from "@mui/x-data-grid/locales";
import { useTheme } from "@mui/material/styles";

// Doble click en filas deshabilitado globalmente para todas las tablas.
// Los handlers `onRowDoubleClick` de cada tabla se mantienen intactos; para
// volver a habilitarlo basta cambiar esta constante a `true`.
const ENABLE_ROW_DOUBLE_CLICK: boolean = false;

interface DataTableProps<T extends GridValidRowModel> {
  rows: GridRowsProp<T>;
  columns: GridColDef[];
  paginationModel: { page: number; pageSize: number };
  processRowUpdate?: (
    newRow: GridRowModel<T>,
    oldRow: GridRowModel<T>
  ) => Promise<T>;
  onProcessRowUpdateError?: (error: unknown) => void;
  onSelectionModelChange: (selectionModel: any) => void;
  slots: Partial<GridSlotsComponent>;
  onPaginationModelChange: (model: { page: number; pageSize: number }) => void;
  rowCount: number;
  checkboxSelection?: boolean;
  disableRowSelectionOnClick?: boolean;
  onRowDoubleClick?: (params: GridRowParams<T>) => void;
  apiRef?: any;
  sortingMode?: "client" | "server";
}

export default function DataTable<T extends GridValidRowModel>({
  rows,
  rowCount,
  columns,
  paginationModel,
  processRowUpdate,
  onProcessRowUpdateError,
  checkboxSelection = true,
  disableRowSelectionOnClick = true,
  onSelectionModelChange,
  onRowDoubleClick,
  apiRef,
  slots,
  onPaginationModelChange,
  sortingMode = "server",
}: DataTableProps<T>) {
  const theme = useTheme();

  const rowDoubleClickHandler = ENABLE_ROW_DOUBLE_CLICK
    ? onRowDoubleClick
    : undefined;
  
  // Color de hover que funciona bien en ambos modos (mismo que usa MUI por defecto)
  const hoverColor = theme.palette.mode === 'dark' 
    ? 'rgba(255, 255, 255, 0.08)' 
    : 'rgba(0, 0, 0, 0.04)';
  
  // Manejar "Todos" (-1) como rowCount
  const effectivePageSize = paginationModel.pageSize === -1 ? rowCount : paginationModel.pageSize;
  const effectivePaginationModel = {
    ...paginationModel,
    pageSize: effectivePageSize,
  };
  
  return (
    <DataGrid
      apiRef={apiRef}
      rows={rows}
      columns={columns}
      initialState={{ pagination: { paginationModel: effectivePaginationModel } }}
      pageSizeOptions={[20, 100, { value: -1, label: "Todos" }]}
      checkboxSelection={checkboxSelection}
      disableRowSelectionOnClick={disableRowSelectionOnClick}
      localeText={esES.components.MuiDataGrid.defaultProps.localeText as Partial<GridLocaleText>}
      sx={{
        border: 0,
        '& .MuiDataGrid-row:hover': {
          backgroundColor: hoverColor,
          ...(rowDoubleClickHandler ? { cursor: 'pointer' } : {}),
        },
      }}
      processRowUpdate={processRowUpdate}
      onProcessRowUpdateError={onProcessRowUpdateError}
      onRowSelectionModelChange={onSelectionModelChange}
      onRowDoubleClick={rowDoubleClickHandler}
      onPaginationModelChange={(newModel) => {
        // Convertir -1 de vuelta cuando se selecciona "Todos"
        const pageSize = newModel.pageSize === rowCount ? -1 : newModel.pageSize;
        onPaginationModelChange({ ...newModel, pageSize });
      }}
      slots={slots}
      hideFooterSelectedRowCount
      paginationMode={sortingMode === "client" ? "client" : "server"}
      sortingMode={sortingMode}
      rowCount={rowCount}
    />
  );
}
